import { usePathname } from "next/navigation";

import { Dialog } from "@calcom/features/components/controlled-dialog";
import { APP_NAME, WEBAPP_URL } from "@calcom/lib/constants";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@calcom/ui/components/button";
import { DialogContent, DialogFooter } from "@calcom/ui/components/dialog";

interface IOverlayCalendarContinueModalProps {
  open?: boolean;
  onClose?: (state: boolean) => void;
  onContinue: () => void;
  hasSession?: boolean;
}

export function OverlayCalendarContinueModal(props: IOverlayCalendarContinueModalProps) {
  const { t } = useLocale();
  const pathname = usePathname();

  const returnUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `${WEBAPP_URL}${pathname ?? ""}`;

  const googleAuthUrl = `/api/guest-calendar/auth?provider=google&returnUrl=${encodeURIComponent(returnUrl)}`;
  const microsoftAuthUrl = `/api/guest-calendar/auth?provider=microsoft&returnUrl=${encodeURIComponent(returnUrl)}`;

  return (
    <>
      <Dialog open={props.open} onOpenChange={props.onClose}>
        <DialogContent
          type="creation"
          title={t("overlay_my_calendar")}
          description={t("overlay_my_calendar_toc")}>
          <div className="flex flex-col gap-2">
            {/* Guest: Google Calendar — no account needed */}
            <a
              href={googleAuthUrl}
              className="flex w-full items-center justify-center gap-2.5 rounded-md border border-default bg-default px-4 py-2.5 text-sm font-semibold text-default shadow-sm hover:bg-subtle transition-colors">
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Connect Google Calendar
            </a>

            {/* Guest: Outlook — no account needed */}
            <a
              href={microsoftAuthUrl}
              className="flex w-full items-center justify-center gap-2.5 rounded-md border border-default bg-default px-4 py-2.5 text-sm font-semibold text-default shadow-sm hover:bg-subtle transition-colors">
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 23 23">
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              Connect Outlook / Office 365
            </a>

            <div className="relative my-1">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-subtle" /></div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-default px-2 text-muted">
                  {props.hasSession ? "or use your Schedly calendars" : "or sign in for full access"}
                </span>
              </div>
            </div>

            {/* Logged-in: open calendar settings; Guest: sign in */}
            <Button
              data-testid="overlay-calendar-continue-button"
              onClick={() => { props.onContinue(); }}
              color="secondary"
              className="w-full items-center justify-center font-semibold"
              StartIcon="calendar-search">
              {props.hasSession ? "Open Calendar Settings" : t("continue_with", { appName: APP_NAME })}
            </Button>
          </div>
          <DialogFooter>
            <></>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
